import {
  deletePushSubscription as sdkDeletePushSubscription,
  getVapidPublicKey as sdkGetVapidPublicKey,
  savePushSubscription as sdkSavePushSubscription,
} from "~/lib/api";

/**
 * Web-push subscribe/unsubscribe flow (V2-E4.1, HAN-63). Wraps the browser Push
 * APIs and the generated SDK (`/push/*`) behind discriminated-union results so
 * the settings screen stays free of API/permission plumbing — mirrors
 * `~/lib/ward-invitations`.
 *
 * The service worker that receives the pushes is HAN-26's Workbox SW, extended
 * with `push`/`notificationclick` handlers via `public/push-sw.js`.
 */

export type PushEnableResult =
  // Subscribed (or already subscribed) and registered with the API.
  | { status: "ok" }
  // This browser can't do web push (no SW / PushManager / Notification).
  | { status: "unsupported" }
  // The user dismissed or blocked the permission prompt.
  | { status: "denied" }
  // The server has no VAPID key configured, so there's nothing to subscribe to.
  | { status: "unconfigured" }
  | { status: "error"; message: string };

export type PushDisableResult = { status: "ok" } | { status: "error"; message: string };

/** Whether this browser supports the APIs the push flow needs. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current notification-permission state, or "denied" when unsupported. */
export function getNotificationPermission(): NotificationPermission {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

/** Whether this browser currently has an active push subscription. */
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

/** The server's VAPID public key, or null when push isn't configured. */
async function fetchPublicKey(): Promise<string | null> {
  const result = await sdkGetVapidPublicKey();
  return result.data?.publicKey ?? null;
}

/**
 * Request permission (if needed), subscribe this browser to push, and register
 * the subscription with the API. Idempotent — re-running with an existing
 * subscription just re-registers it (the API upserts).
 */
export async function enablePush(): Promise<PushEnableResult> {
  if (!isPushSupported()) return { status: "unsupported" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { status: "denied" };

  const publicKey = await fetchPublicKey();
  if (!publicKey) return { status: "unconfigured" };

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const { endpoint, keys } = toSubscriptionJson(subscription);
    const result = await sdkSavePushSubscription({ body: { endpoint, keys } });
    if (result.error) {
      return { status: "error", message: "Couldn't register for notifications. Please try again." };
    }
    return { status: "ok" };
  } catch {
    return { status: "error", message: "Couldn't enable notifications on this device." };
  }
}

/** Unsubscribe this browser and remove the subscription from the API. */
export async function disablePush(): Promise<PushDisableResult> {
  if (!isPushSupported()) return { status: "ok" };
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      // Best-effort server cleanup first, then drop the browser subscription.
      await sdkDeletePushSubscription({ body: { endpoint: subscription.endpoint } });
      await subscription.unsubscribe();
    }
    return { status: "ok" };
  } catch {
    return { status: "error", message: "Couldn't turn off notifications. Please try again." };
  }
}

/** Pull `{ endpoint, keys: { p256dh, auth } }` out of a PushSubscription. */
function toSubscriptionJson(subscription: PushSubscription): {
  endpoint: string;
  keys: { p256dh: string; auth: string };
} {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
  };
}

/**
 * VAPID public keys travel as base64url; `pushManager.subscribe` wants the raw
 * bytes as a Uint8Array. Standard conversion (pad, swap url-safe chars, decode).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Back the array with a concrete ArrayBuffer so it satisfies BufferSource
  // (applicationServerKey rejects a SharedArrayBuffer-backed view).
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}
